/**
 * Streaks — adherence-first, founder-defined-pause, tiered thresholds.
 *
 * Three streak concepts (only the primary is celebrated by default):
 *
 *   1. Adherence Streak (primary)
 *      Consecutive scheduled days where the user did the right thing:
 *        - completed a scheduled workout, OR
 *        - logged a scheduled rest day, OR
 *        - active "pause" covering that date (user-defined start/end calendar).
 *      Breaks on: unscheduled skip of a workout day with no pause covering it.
 *
 *   2. Showing-Up Streak (visible-only, not celebrated)
 *      Consecutive days with ANY logged action.
 *
 *   3. Best-Ever Streak (parallel metric)
 *      Longest adherence streak achieved ever. Never reset.
 *
 * Tiered thresholds (scale by `profile.experience`):
 *   beginner       7d / 14d / 30d / 60d
 *   intermediate   4w / 8w / 12w / 26w
 *   advanced       12w / 26w / 52w / 104w
 *
 * Easter egg: Once a user logs 2 consecutive programs completed (≥26 weeks
 * total of program adherence with no breaking pause), an Expert tier unlocks
 * — celebrating program-completion count rather than raw weeks. See
 * `programsCompletedStreak`.
 *
 * --------------------------------------------------------------------------
 * Pause model
 * --------------------------------------------------------------------------
 *
 * `profile.streakPauses: [{ id, start: 'YYYY-MM-DD', end: 'YYYY-MM-DD'|null,
 *   reason?, note? }]`
 *
 * - `end: null` means "open-ended" (currently paused). User picks an end date
 *   when they resume.
 * - No app-imposed limit on pause length. The brand stance: the user knows
 *   their body. Future contest features can impose contest-specific limits.
 *
 * --------------------------------------------------------------------------
 * Program completion (Easter egg path to Expert tier)
 * --------------------------------------------------------------------------
 *
 * `profile.programCompletions: [{ id, programId, startedAt, completedAt, weeks }]`
 *
 * Triggered when a user finishes a full program cycle. Two consecutive
 * completions with no >30d gap between them unlocks Expert tier and the
 * "programs completed streak" metric.
 */

import { localISO } from '../data/programs';

// ----- Date helpers (YYYY-MM-DD ISO, TZ-stable) -----
const dayMS = 86400000;
const toISO = (d) => {
  if (typeof d === 'string') return d.slice(0, 10);
  const x = d instanceof Date ? d : new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDaysISO = (iso, n) => toISO(new Date(new Date(iso + 'T00:00:00').getTime() + n * dayMS));
const diffDays = (aISO, bISO) => Math.round((new Date(aISO + 'T00:00:00') - new Date(bISO + 'T00:00:00')) / dayMS);

// ----- Pause logic -----
export const isPaused = (pauses, iso) => {
  for (const p of pauses || []) {
    if (!p.start) continue;
    const ends = p.end || '9999-12-31';
    if (p.start <= iso && iso <= ends) return true;
  }
  return false;
};
export const currentOpenPause = (pauses) => (pauses || []).find((p) => p.start && !p.end) || null;

// ----- Calendar helpers -----
// Given the user's program schedule (DAYS array of 7 entries: 'strength'|'rest'|
// 'recovery'|'martial'|...), return the kind for a given dayOfWeek (0=Sun).
const dayKindFor = (schedule, jsDow) => (schedule && schedule[jsDow]) || 'rest';

// ----- Streak computation -----
/**
 * computeAdherenceStreak(history, schedule, pauses, todayISO?)
 *
 * Walks backward from today, day by day, deciding "did this day count as kept?"
 * Stops at the first un-kept day (or returns total length).
 *
 * @param history list of completed sessions { date: 'YYYY-MM-DD', type }
 * @param schedule the user's weekly schedule
 * @param pauses pauses array
 * @param todayISO optional override for testing
 */
export const computeAdherenceStreak = (history, schedule, pauses, todayISO = localISO()) => {
  const completedDays = new Set((history || []).map((h) => toISO(h.date)));
  let streak = 0;
  let cursor = todayISO;
  let firstDayChecked = true;
  while (true) {
    const d = new Date(cursor + 'T00:00:00');
    const dow = d.getDay();
    const kind = dayKindFor(schedule, dow);
    const paused = isPaused(pauses, cursor);
    const logged = completedDays.has(cursor);

    if (paused) {
      // Paused days extend the chain but don't add to count — the streak holds.
      cursor = addDaysISO(cursor, -1);
      firstDayChecked = false;
      continue;
    }

    if (kind === 'rest' || kind === 'recovery') {
      // Scheduled rest day: counted as kept whether logged or not — the
      // schedule said to rest, the user rested.
      streak += 1;
    } else if (logged) {
      streak += 1;
    } else {
      // First-day-only grace: if TODAY is a workout day and not yet logged,
      // we don't break the streak — the day isn't over.
      if (firstDayChecked && cursor === todayISO) {
        cursor = addDaysISO(cursor, -1);
        firstDayChecked = false;
        continue;
      }
      break;
    }
    cursor = addDaysISO(cursor, -1);
    firstDayChecked = false;
    // Hard safety stop at 5 years
    if (streak > 1830) break;
  }
  return streak;
};

/**
 * computeShowingUpStreak — much simpler: any logged action on a day = +1.
 * Pause days count as kept (same as adherence).
 */
export const computeShowingUpStreak = (history, pauses, todayISO = localISO()) => {
  const completedDays = new Set((history || []).map((h) => toISO(h.date)));
  let streak = 0;
  let cursor = todayISO;
  let firstDayChecked = true;
  while (true) {
    if (isPaused(pauses, cursor)) {
      cursor = addDaysISO(cursor, -1);
      firstDayChecked = false;
      continue;
    }
    if (completedDays.has(cursor)) {
      streak += 1;
    } else if (firstDayChecked && cursor === todayISO) {
      // Today not yet logged — grace
      cursor = addDaysISO(cursor, -1);
      firstDayChecked = false;
      continue;
    } else {
      break;
    }
    cursor = addDaysISO(cursor, -1);
    firstDayChecked = false;
    if (streak > 1830) break;
  }
  return streak;
};

// ----- Thresholds -----
// Returns the highest milestone newly crossed (number of units), or null.
// units: 'days' or 'weeks'.
const BEGINNER_THRESHOLDS = { days: [7, 14, 30, 60] };
const INTERMEDIATE_THRESHOLDS = { weeks: [4, 8, 12, 26] };
const ADVANCED_THRESHOLDS = { weeks: [12, 26, 52, 104] };

export const tieredThresholds = (experience = 'intermediate') => {
  if (experience === 'beginner') return BEGINNER_THRESHOLDS;
  if (experience === 'advanced' || experience === 'expert') return ADVANCED_THRESHOLDS;
  return INTERMEDIATE_THRESHOLDS;
};

/**
 * Given a current streak length (days) + experience tier + the last celebrated
 * value (from profile.streakLastCelebrated || 0), return the newly-crossed
 * milestone value (in display units) or null.
 *
 * Returns { value, unit } e.g. { value: 4, unit: 'weeks' }
 */
export const newlyCrossedStreakMilestone = (streakDays, experience, lastCelebratedDays = 0) => {
  if (streakDays <= lastCelebratedDays) return null;
  const thresholds = tieredThresholds(experience);
  if (thresholds.days) {
    for (const d of thresholds.days) {
      if (streakDays >= d && lastCelebratedDays < d) {
        return { value: d, unit: 'days', days: d };
      }
    }
  }
  if (thresholds.weeks) {
    for (const w of thresholds.weeks) {
      const d = w * 7;
      if (streakDays >= d && lastCelebratedDays < d) {
        return { value: w, unit: 'weeks', days: d };
      }
    }
  }
  return null;
};

// ----- Program completion (Easter egg → Expert tier) -----
const MAX_PROGRAM_GAP_DAYS = 30; // > 30 day gap breaks the program-consecutive chain
export const consecutiveProgramsCompleted = (completions) => {
  if (!completions || completions.length === 0) return 0;
  const sorted = [...completions].sort((a, b) => (a.completedAt || '').localeCompare(b.completedAt || ''));
  let chain = 1;
  for (let i = 1; i < sorted.length; i++) {
    const gap = diffDays(sorted[i].startedAt || sorted[i].completedAt, sorted[i - 1].completedAt);
    if (gap <= MAX_PROGRAM_GAP_DAYS) chain += 1;
    else chain = 1;
  }
  return chain;
};

/** Two consecutive completions unlocks the Expert tier. */
export const hasUnlockedExpertTier = (completions) => consecutiveProgramsCompleted(completions) >= 2;

// ----- Program completion detection -----
// Default program length when a program doesn't declare one. 12 weeks is the
// most common periodization block in the strength world (Wendler 5/3/1, RTS,
// most hypertrophy blocks). Programs can override via `weeksTarget`.
const DEFAULT_PROGRAM_WEEKS = 12;

/**
 * detectProgramCompletion(profile, todayISO?)
 *
 * Returns a candidate completion object when the user has crossed the
 * program's target duration AND hasn't already logged a completion for this
 * exact (programId, startedAt) tuple AND isn't snoozing the prompt.
 *
 * Returns null otherwise. The user must explicitly confirm — the brand axiom
 * is "user defines what counts," so the app never silently auto-completes.
 *
 * @param profile the full profile object (needs programId, startDate,
 *                programWeeksTarget?, programCompletions, programCompletionSnoozedUntil?)
 * @param todayISO optional override for testing
 * @returns { programId, startedAt, completedAt, weeks } | null
 */
export const detectProgramCompletion = (profile, todayISO = localISO()) => {
  if (!profile || !profile.programType || !profile.startDate) return null;

  const weeksTarget = profile.programWeeksTarget || DEFAULT_PROGRAM_WEEKS;
  const daysElapsed = diffDays(todayISO, profile.startDate);
  if (daysElapsed < 0) return null;
  const weeksElapsed = Math.floor(daysElapsed / 7);
  if (weeksElapsed < weeksTarget) return null;

  // Already logged a completion for this exact (programType, startedAt)?
  const alreadyLogged = (profile.programCompletions || []).some(
    (c) => c.programId === profile.programType && c.startedAt === profile.startDate
  );
  if (alreadyLogged) return null;

  // Snoozed? (user picked "Not yet — extending" recently)
  if (profile.programCompletionSnoozedUntil && todayISO < profile.programCompletionSnoozedUntil) {
    return null;
  }

  return {
    programId: profile.programType,
    startedAt: profile.startDate,
    completedAt: todayISO,
    weeks: weeksElapsed,
  };
};
